import React from 'react';

import { InternalRefetchQueriesInclude } from '@apollo/client';
import { Box, Button, Typography } from '@mui/material';
import { isFunction } from 'es-toolkit';

import { Field, Form } from '@hrlogics/react-components/form';

import { Nullable } from '@clearverify/types';

import {
  InboxItemDocument,
  InboxItemsScrollableListDocument,
  useCreateDataDisputeByEmployeeMutation,
} from 'src/api';
import { TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { useInboxItemParamId } from 'src/hooks/inboxItem';
import { useInboxItemsQueryVariables } from 'src/hooks/inboxItem/useInboxItemsQueryVariables';
import { useModal } from 'src/hooks/shared';

import { FormField, FormValues } from './types';
import { formValidationSchema } from './validation';

export interface OpenDataDisputeModalFormProps {
  onCloseModal: () => void;
  switchClosability: ReturnType<typeof useModal>['switchClosability'];
  employerId: Nullable<string>;
  cb: unknown;
}

export const OpenDataDisputeModalForm: React.FC<OpenDataDisputeModalFormProps> = ({
  switchClosability,
  onCloseModal,
  employerId,
  cb,
}) => {
  const { createApolloContext } = useNotification();

  const inboxItemId = useInboxItemParamId();
  const inboxItemsQueryVariables = useInboxItemsQueryVariables();

  const [createDataDisputeByEmployee] = useCreateDataDisputeByEmployeeMutation({
    context: createApolloContext({ successMessage: 'Data Dispute created successfully' }),
  });

  const handleSubmit = React.useCallback(
    async (formValues: FormValues) => {
      if (!employerId) return;

      switchClosability(false);

      const refetchQueries: InternalRefetchQueriesInclude = [
        { query: InboxItemsScrollableListDocument, variables: inboxItemsQueryVariables },
      ];

      if (inboxItemId) {
        refetchQueries.push(
          {
            query: InboxItemsScrollableListDocument,
            variables: {
              where: {
                id: { equals: inboxItemId },
              },
            },
          },
          {
            query: InboxItemDocument,
            variables: {
              id: inboxItemId,
            },
          },
        );
      }

      try {
        await createDataDisputeByEmployee({
          variables: {
            inboxItemId,
            employerId,
            reason: formValues.reason,
          },
          refetchQueries,
          awaitRefetchQueries: true,
        });

        switchClosability(true);

        onCloseModal();

        if (isFunction(cb)) {
          cb();
        }
      } catch (error) {
        console.error(error);

        switchClosability(true);

        onCloseModal();
      }
    },
    [
      switchClosability,
      inboxItemsQueryVariables,
      inboxItemId,
      createDataDisputeByEmployee,
      employerId,
      onCloseModal,
      cb,
    ],
  );

  return (
    <Form<FormValues>
      onSubmit={handleSubmit}
      validationSchema={formValidationSchema}
      subscription={{ valid: true, submitting: true }}
    >
      {({ submitting, valid }) => (
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="body2" fontSize="24px">
            Confirm you want to submit a Data Dispute
          </Typography>
          <Typography variant="body1" fontSize="16px" color="text.secondary">
            Please, state the reason of Data Dispute
          </Typography>
          <Field
            name={FormField.reason}
            fullWidth
            required
            label="Note to Employer"
            component={TextField}
          />
          <Box display="flex" gap={3} justifyContent="center" alignItems="center">
            <Button color="primary" variant="outlined" onClick={onCloseModal} disabled={submitting}>
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              type="submit"
              disabled={submitting || !valid}
              loading={submitting}
            >
              Yes, dispute
            </Button>
          </Box>
        </Box>
      )}
    </Form>
  );
};
